import React, { useEffect, useMemo } from 'react';
import { loadPbrMaps, disposePbrMaps, ensureUv2, PBR_SETS, PAINT_SET_KEY, useTintProps } from './pbrTextures.jsx';
import { AoStrip, AO } from './ambientOcclusion.jsx';
import { FloorMaterialBox } from './Floor3D.jsx';

// هر نوعِ پوششِ «واقعی/PBR» به یکی از ستِ‌های pbrTextures.js وصل می‌شود — افزودنِ یک بافتِ واقعیِ تازه
// (مثلاً سنگ یا پارکتِ واقعی) فقط یعنی یک ردیفِ تازه این‌جا، بدونِ دست‌زدن به بقیه‌ی منطقِ CoveringLayer
const PBR_WALL_TYPE_TO_SET = {
  ceramicPbr: 'ceramicTileWhite',
  brickPbr: 'brickRed',
  concretePbr: 'concreteGray',
  stoneSlabPbr: 'marbleVeined',
  stonePbr: 'graniteSpeckled',
  travertinePbr: 'travertine',
  // روکشِ چوبی/لمبه‌ی دیوار — همان چوبِ کاجِ در/پنجره؛ رگه‌اش عمودی است که برای دیوار هم طبیعی است
  woodPbr: 'woodPine',
  woodDarkPbr: 'woodDark',
  plasterPbr: 'plasterWhite',
  wallpaperPbr: 'wallpaper',
};

// پوشش‌های کدنویسی‌شده‌ی قدیمی (canvas) حذف شده‌اند؛ دیوارهای ذخیره‌شده با آن نوع‌ها خودکار به نزدیک‌ترین
// جنسِ واقعی (یا رنگِ ساده، وقتی معادلِ واقعی ندارد) نشان داده می‌شوند. wallStyleBox.jsx هم از همین
// تابع استفاده می‌کند تا دکمه‌ی درست فعال دیده شود
const LEGACY_WALL_TYPES = {
  tile: 'ceramicPbr',
  brick: 'brickPbr',
  stone: 'stonePbr',
  stoneSlab: 'stoneSlabPbr',
  stonePlain: 'paint',
  wallpaper: 'wallpaperPbr',
};

export function normalizeWallCoveringType(type) {
  const t = LEGACY_WALL_TYPES[type] || type || 'paint';
  return t === 'paint' || PBR_WALL_TYPE_TO_SET[t] ? t : 'paint';
}

// این فایل «المان دیوار» است. هر تکه از دیوار یک هسته‌ی ساختاریِ خنثی دارد (فقط برای این‌که لبه‌ها/
// نبش‌ها توپر دیده شوند) به‌اضافه‌ی دو رویه‌ی کاملاً مستقل — یکی برای هر سمتِ دیوار (sides.a و
// sides.b). این جداییِ دو رویه، دقیقاً همان چیزی است که باعث می‌شود رنگ/جنس/قرنیزِ یک سمتِ دیوار
// (مثلاً سمتِ اتاق‌خواب) هیچ اثری روی سمتِ دیگر (مثلاً سمتِ پذیرایی) نگذارد.

const DEFAULT_WALL_COLOR = '#ffffff';
const DEFAULT_ACCENT_COLOR = '#cdbfae';
export const DEFAULT_BASEBOARD_COLOR = '#cdc6b8';
export const DEFAULT_BASEBOARD_HEIGHT_CM = 9;
export const CORE_COLOR = '#efebe3'; // گچِ سفیدِ کمی گرم — حالا نور می‌گیرد، پس خودش باید روشن‌تر از قبل باشد
export const FACE_OFFSET_CM = 0.4;
export const BASEBOARD_DEPTH_CM = 1.5;
// شدتِ سایه‌روشنِ رویه‌ی دیوارها نصف می‌شود (خواسته‌ی کاربر). عددِ ۰٫۳۸ با رندرِ واقعی (three r186 + ACES)
// کالیبره شده: به‌خاطرِ غیرخطی‌بودنِ tone mapping، ۰٫۳۸ عمقِ سایه‌ی دیده‌شده روی صفحه را دقیقاً نصف می‌کند
// (۰٫۵ حدودِ ۶۰٪ کمش می‌کرد). ثابت در سطحِ ماژول (نه شیءِ تازه در هر رندر) تا R3F هر بار userData را عوض
// نکند. نگاه کنید به roomLighting.jsx
export const WALL_SHADE_USERDATA = { khShadeSoften: 0.38 }; // فاصله‌ی ناچیزِ رویه از سطح هسته، فقط برای جلوگیری از z-fighting

// حالتِ پیش‌فرضِ کاملِ یک رویه — وقتی کاربر هنوز هیچ تنظیمی برای این سمت نکرده
export const DEFAULT_WALL_SIDE_STYLE = {
  main: { type: 'paint', color: DEFAULT_WALL_COLOR, accent: DEFAULT_ACCENT_COLOR },
  lower: null,
  finish: 'matte',
  baseboard: { enabled: false, color: DEFAULT_BASEBOARD_COLOR, height: DEFAULT_BASEBOARD_HEIGHT_CM },
};

// دیوارهای قدیمی (قبل از این‌که هر دیوار دو رویه‌ی مستقل داشته باشد) فقط یک wall.color/wall.finish/
// wall.baseboard مشترک داشتند. این تابع اگر wall.sides نباشد ولی این فیلدهای قدیمی موجود باشند،
// همان مقدار قدیمی را به هر دو سمت نسبت می‌دهد — تا دیوارهایی که کاربر قبلاً رنگ کرده بود، بعد از این
// تغییر ناگهان سفید/بدون‌تنظیم دیده نشوند. برای دیوار تازه (بدون هیچ‌کدام) null برمی‌گرداند.
export function resolveWallSides(wall) {
  if (wall.sides) return wall.sides;
  if (wall.color || wall.finish || wall.baseboard) {
    const legacy = {
      main: { type: 'paint', color: wall.color || DEFAULT_WALL_COLOR, accent: DEFAULT_ACCENT_COLOR },
      lower: null,
      finish: wall.finish || 'matte',
      baseboard: wall.baseboard || { enabled: false },
    };
    return { a: legacy, b: legacy };
  }
  return null;
}

export function resolveSideStyle(side) {
  if (!side) return DEFAULT_WALL_SIDE_STYLE;
  return {
    main: side.main || DEFAULT_WALL_SIDE_STYLE.main,
    lower: side.lower || null,
    finish: side.finish || 'matte',
    baseboard: side.baseboard || { enabled: false },
  };
}

// یک لایه‌ی پوشش (رنگِ ساده یا یک جنسِ واقعیِ PBR) روی یک بخشِ ارتفاعیِ مشخص از یک رویه.
//
// نکته‌ی مهم درباره‌ی «الگو باید پیوسته باشد»: هر تکه از دیوار (قبل/بعد از در یا پنجره، بالای در،
// زیر/بالای پنجره، پوشش پایینی/اصلی) یک مشِ جدا با UVِ صفر-تا-یکِ خودش است. اگر فازِ تکرارِ بافت را
// روی همین UVِ محلی حساب کنیم، هر تکه الگو را از نو شروع می‌کند و درست همان‌جا که دو تکه به هم می‌رسند
// (مثلاً لبه‌ی بالای در) درزِ کاشی/آجر می‌پرد. راه‌حل: فازِ الگو از موقعیتِ واقعیِ این تکه روی دیوار
// (alongStart) و روی ارتفاع (vertStart) گرفته می‌شود؛ چون این دو بینِ دو تکه‌ی هم‌مرز پیوسته‌اند، بافت
// بدونِ جهش ادامه پیدا می‌کند — انگار کلِ رویه یک بافتِ واحد دارد.
// geometry: (اختیاری) هندسه‌ی آماده با uvِ ۰ تا ۱ روی طول/ارتفاع — برای رویه‌ی دیوارِ منحنی (CurvedWall3D)
export function CoveringLayer({ layer, length, height, alongStart, vertStart, z, rotationY, finish, geometry = null }) {
  const type = normalizeWallCoveringType(layer?.type);
  const color = layer?.color || DEFAULT_WALL_COLOR;
  const isPbr = Boolean(PBR_WALL_TYPE_TO_SET[type]);
  // «رنگ» هم رنگِ واقعی است: بافتِ سطحِ رنگ‌شده (pbrTextures.PAINT_SET_KEY) زیرِ همان رنگِ انتخابی
  const setKey = isPbr ? PBR_WALL_TYPE_TO_SET[type] : PAINT_SET_KEY;

  // واحدِ تکرار = اندازه‌ی واقعیِ کلِ عکس روی دیوار (baseSizeCm در pbrTextures.jsx). عمداً به حداقلِ ۱
  // محدود نمی‌شود، چون همین مقدارِ دقیقِ length/unitCm است که فازِ الگو را بینِ تکه‌ها درست جمع می‌کند
  const unitCm = PBR_SETS[setKey].baseSizeCm;
  const repeatX = length / unitCm;
  const repeatY = height / unitCm;
  // فقط قسمتِ اعشاری لازم است؛ (((x % 1) + 1) % 1) تا برای مقادیرِ منفی هم بینِ ۰ و ۱ بماند
  const offsetX = (((alongStart / unitCm) % 1) + 1) % 1;
  const offsetY = (((vertStart / unitCm) % 1) + 1) % 1;

  const pbrMaps = useMemo(() => {
    const maps = loadPbrMaps(PBR_SETS[setKey], repeatX, repeatY);
    Object.values(maps).forEach((t) => t?.offset.set(offsetX, offsetY));
    return maps;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setKey, repeatX, repeatY, offsetX, offsetY]);

  useEffect(() => () => disposePbrMaps(pbrMaps), [pbrMaps]);

  // رنگ‌آمیزیِ هوشمند (فقط برای جنس‌های درزدار مثلِ کاشی/آجر): درز/ملات رنگِ طبیعی‌اش را نگه می‌دارد
  const tintProps = useTintProps(isPbr ? PBR_SETS[setKey] : null, pbrMaps, color);

  if (length <= 0 || height <= 0) return null;

  const centerAlong = alongStart + length / 2;
  const centerY = vertStart + height / 2;
  const isGlossy = finish === 'glossy' && type === 'paint';

  return (
    <mesh position={geometry ? [0, 0, 0] : [centerAlong, centerY, z]} rotation={[0, geometry ? 0 : rotationY, 0]} geometry={geometry || undefined}>
      {!geometry && <planeGeometry ref={ensureUv2} args={[length, height]} />}
      {/* هر حالت یک keyِ ثابتِ خودش دارد — اگر یک متریال با propsِ ناقص/متفاوت دوباره‌استفاده شود،
          نتیجه سیاه می‌شود (همان باگی که قبلاً روی سقف افتاد) */}
      {isPbr ? (
        <meshStandardMaterial
          userData={WALL_SHADE_USERDATA}
          key={`pbr-${type}`}
          {...tintProps}
          map={pbrMaps?.map}
          normalMap={pbrMaps?.normalMap}
          roughnessMap={pbrMaps?.roughnessMap}
          aoMap={pbrMaps?.aoMap}
          roughness={1}
          metalness={0}
          envMapIntensity={0.5}
        />
      ) : isGlossy ? (
        // رنگِ براق: همان بافتِ رنگِ واقعی (برجستگی)، ولی زبریِ پایینِ یکنواخت به‌جای نقشه‌ی زبریِ مات
        <meshStandardMaterial
          userData={WALL_SHADE_USERDATA}
          key="paint-glossy"
          color={color}
          map={pbrMaps?.map}
          normalMap={pbrMaps?.normalMap}
          roughness={0.18}
          metalness={0.05}
          envMapIntensity={0.8}
        />
      ) : (
        // رنگِ مات: بافت + زبریِ واقعیِ دیوارِ رنگ‌شده. قبلاً این‌جا meshBasicMaterial بود — یعنی دیوار
        // اصلاً به نور واکنش نشان نمی‌داد (گوشه‌ها و وسط همان‌قدر روشن)؛ حالا مثلِ دیوارِ واقعی نور/سایه می‌گیرد
        <meshStandardMaterial
          userData={WALL_SHADE_USERDATA}
          key="paint-matte"
          color={color}
          map={pbrMaps?.map}
          normalMap={pbrMaps?.normalMap}
          roughnessMap={pbrMaps?.roughnessMap}
          roughness={1}
          metalness={0}
          envMapIntensity={0.4}
        />
      )}
    </mesh>
  );
}

// یک رویه (فقط یک سمتِ دیوار) — یک یا دو لایه‌ی پوشش (اگر «پوشش دوم/پایینی»، مثلاً کاشیِ تا نصفه،
// تنظیم شده باشد) به‌اضافه‌ی قرنیزِ مخصوصِ همین سمت. faceSign مشخص می‌کند این رویه رو به کدام سمتِ
// دیوار است (+۱ یا -۱ روی محور محلیِ Z، همان جهتِ ضخامتِ دیوار)
// ao: { top, start, end } — «سایه‌ی کنج» (ambientOcclusion.jsx): پایینِ رویه (محلِ اتصال به کف) همیشه وقتی این
// تکه از کف شروع شود؛ بالا (اتصال به سقف) و دو سرِ دیوار (گوشه‌های اتاق) فقط وقتی از بیرون خواسته شود
// floorLevel: ارتفاعِ کفِ اتاقِ همین سمتِ دیوار (اختلافِ سطح، Floor3D.jsx) — قرنیز، سایه‌ی پایینِ دیوار و خطِ
// «پوششِ پایینی» از روی همین کف حساب می‌شوند، نه از y=۰
function WallFace({ side, length, height, bottom, thickness, centerAlong, faceSign, onSelect, ao, floorLevel = 0 }) {
  if (length <= 0 || height <= 0) return null;
  const s = resolveSideStyle(side);
  const z = faceSign * (thickness / 2 + FACE_OFFSET_CM);
  const rotationY = faceSign > 0 ? 0 : Math.PI;
  const alongStart = centerAlong - length / 2;
  const top = bottom + height;

  // «پوشش پایینی» (مثلاً کاشیِ تا نصفه) نسبت به کفِ واقعیِ اتاق (y=۰) تعریف می‌شود، نه نسبت به این
  // تکه‌ی خاص از دیوار. برای همین همپوشانیِ بازه‌ی [bottom, top] این تکه با بازه‌ی [۰, ارتفاعِ پوشش]
  // را حساب می‌کنیم — اگر این تکه کلاً زیرِ خطِ پوشش باشد (مثلاً زیرِ رفِ پنجره) کاملاً پوشش پایینی
  // می‌گیرد، اگر کلاً بالای آن باشد (مثلاً بالای در/پنجره) اصلاً پوشش پایینی نمی‌گیرد، و اگر خط از
  // وسطش رد شود هر دو لایه را به‌اندازه‌ی سهم‌شان می‌گیرد
  const splitLine = s.lower ? floorLevel + s.lower.heightCm : null;
  const lowerTop = splitLine != null ? Math.min(Math.max(splitLine, bottom), top) : bottom;
  const lowerHeight = Math.max(0, lowerTop - bottom);
  const mainHeight = Math.max(0, top - (bottom + lowerHeight));
  // آیا این تکه از کفِ همین سمت شروع می‌شود (یا از زیرش، وقتی دیوار تا پایین‌ترین کفِ خانه ادامه دارد)
  const touchesFloor = bottom <= floorLevel + 0.5 && top > floorLevel;
  const visibleH = top - Math.max(bottom, floorLevel);
  const baseboardHeight = Math.max(1, Math.min(visibleH, s.baseboard?.height || DEFAULT_BASEBOARD_HEIGHT_CM));

  // نوارهای سایه‌ی کنج، کمی جلوترِ خودِ پوشش (تا روی آن بیفتند)
  const zAo = faceSign * (thickness / 2 + FACE_OFFSET_CM + 0.25);
  const aoBottomH = Math.min(AO.wallBottomHeight, visibleH);
  const aoTopH = Math.min(AO.wallTopHeight, height);
  const aoCornerW = Math.min(AO.cornerWidth, length / 2);
  // در رویه‌ی b (چرخیده ۱۸۰ درجه)، «سمتِ شروعِ دیوار» در مختصاتِ خودِ نوار برعکس است
  const towardStart = faceSign > 0 ? -Math.PI / 2 : Math.PI / 2;
  const towardEnd = -towardStart;

  // با تپ روی این رویه، فقط همین سمتِ دیوار انتخاب می‌شود — stopPropagation تا نه به رویه‌ی پشتی برسد
  // (که اصلاً از این‌جا قابل لمس نیست، ولی برای اطمینان) و نه به «جای خالی» (onPointerMissed) تعبیر شود
  const handleClick = (e) => {
    e.stopPropagation();
    if (onSelect) onSelect(faceSign > 0 ? 'a' : 'b');
  };

  return (
    <group onClick={handleClick}>
      {lowerHeight > 0 && (
        <CoveringLayer
          layer={s.lower}
          length={length}
          height={lowerHeight}
          alongStart={alongStart}
          vertStart={bottom}
          z={z}
          rotationY={rotationY}
          finish={s.finish}
        />
      )}
      {mainHeight > 0 && (
        <CoveringLayer
          layer={s.main}
          length={length}
          height={mainHeight}
          alongStart={alongStart}
          vertStart={bottom + lowerHeight}
          z={z}
          rotationY={rotationY}
          finish={s.finish}
        />
      )}
      {touchesFloor && (
        <AoStrip position={[centerAlong, floorLevel + aoBottomH / 2, zAo]} rotation={[0, rotationY, 0]} size={[length, aoBottomH]} opacity={AO.wallBottomOpacity} />
      )}
      {ao?.top && (
        <AoStrip position={[centerAlong, top - aoTopH / 2, zAo]} rotation={[0, rotationY, Math.PI]} size={[length, aoTopH]} opacity={AO.wallTopOpacity} />
      )}
      {ao?.start && (
        <AoStrip position={[alongStart + aoCornerW / 2, bottom + height / 2, zAo]} rotation={[0, rotationY, towardStart]} size={[height, aoCornerW]} opacity={AO.cornerOpacity} />
      )}
      {ao?.end && (
        <AoStrip position={[alongStart + length - aoCornerW / 2, bottom + height / 2, zAo]} rotation={[0, rotationY, towardEnd]} size={[height, aoCornerW]} opacity={AO.cornerOpacity} />
      )}
      {touchesFloor && s.baseboard?.enabled && (
        // قرنیز: ۱٫۵ سانت ضخامت (واقعی) — نازک‌تر از زهوارِ دورِ در (۲ سانت)، پس درست مثلِ اجرای واقعی
        // پشتِ زهوار تمام می‌شود و از آن بیرون نمی‌زند. با نور (نه رنگِ تختِ بی‌سایه)
        <mesh position={[centerAlong, floorLevel + baseboardHeight / 2, faceSign * (thickness / 2 + FACE_OFFSET_CM + BASEBOARD_DEPTH_CM / 2)]}>
          <boxGeometry args={[length, baseboardHeight, BASEBOARD_DEPTH_CM]} />
          <meshStandardMaterial color={s.baseboard.color || DEFAULT_BASEBOARD_COLOR} roughness={0.6} metalness={0} envMapIntensity={0.3} />
        </mesh>
      )}
    </group>
  );
}

// یک تکه از دیوار — هسته‌ی ساختاریِ خنثی + دو رویه‌ی کاملاً مستقل (sides.a و sides.b)
// aoTop/aoStart/aoEnd: سایه‌ی کنج در بالای این تکه (اتصال به سقف) و در دو سرِ دیوار (گوشه‌های اتاق)
// floorLevels: { a, b } — ارتفاعِ کفِ اتاقِ دو طرفِ این تکه (اختلافِ سطح)؛ پیش‌فرض هر دو صفر
// hidden: { a: [[start, end] …], b: […] } — بازه‌هایی (روی طولِ همین دیوار) که رویه‌ی آن سمت نباید ساخته شود،
// چون دیوارِ مشترکِ هم‌پوشان (دیوارِ اتاقِ همان سمت) دقیقاً همان‌جا رویه‌ی خودش را دارد. دو رویه‌ی رویِ‌هم در
// یک صفحه باعثِ «لرزش» (z-fighting) می‌شدند — مخصوصاً وقتی یکی پوشش/رنگِ دیگری داشت
function visiblePieces(start, end, ranges) {
  let pieces = [[start, end]];
  (ranges || []).forEach(([lo, hi]) => {
    const next = [];
    pieces.forEach(([a, b]) => {
      if (hi <= a || lo >= b) {
        next.push([a, b]);
        return;
      }
      if (lo > a) next.push([a, lo]);
      if (hi < b) next.push([hi, b]);
    });
    pieces = next;
  });
  return pieces.filter(([a, b]) => b - a > 0.5);
}

function WallSegment({ length, height, thickness, centerAlong, bottom = 0, sides, onSelectSide, aoTop, aoStart, aoEnd, floorLevels, hidden }) {
  if (length <= 0 || height <= 0) return null;
  const segStart = centerAlong - length / 2;
  const segEnd = centerAlong + length / 2;
  const faces = (side, faceSign) =>
    visiblePieces(segStart, segEnd, hidden && hidden[side]).map(([a, b]) => (
      <WallFace
        key={`${side}-${a}`}
        side={sides?.[side]}
        length={b - a}
        height={height}
        bottom={bottom}
        thickness={thickness}
        centerAlong={(a + b) / 2}
        faceSign={faceSign}
        onSelect={onSelectSide}
        ao={{ top: Boolean(aoTop), start: Boolean(aoStart) && a <= segStart + 0.01, end: Boolean(aoEnd) && b >= segEnd - 0.01 }}
        floorLevel={floorLevels?.[side] || 0}
      />
    ));

  return (
    <group>
      {/* هسته‌ی دیوار — رویه‌های بغلِ آن (لبه‌ی گچیِ دورِ پنجره/بازشو، سرِ آزادِ دیوار) واقعاً دیده می‌شوند؛
          قبلاً meshBasic (بدونِ نور) بود و این لبه‌ها یک رنگِ تختِ یک‌دست و «کارتونی» داشتند. حالا مثلِ گچِ
          واقعی نور/سایه می‌گیرد */}
      <mesh position={[centerAlong, bottom + height / 2, 0]}>
        <boxGeometry args={[length, height, thickness]} />
        <meshStandardMaterial color={CORE_COLOR} roughness={0.95} metalness={0} envMapIntensity={0.4} userData={WALL_SHADE_USERDATA} />
      </mesh>
      {faces('a', 1)}
      {faces('b', -1)}
    </group>
  );
}

// «آستانه» زیرِ در/بازشو — همان پله‌ی اختلافِ سطح، با «جنس و رنگِ کفِ اتاقِ بالاتر» (پله ادامه‌ی همان کف
// است). عمداً مثلِ پله‌ی واقعی ساخته شده تا چشم آن را «پله» بفهمد، نه یک سطحِ نرم/منحنی:
//   • بدنه از پایین‌ترین کفِ خانه تا زیرِ لایه‌ی رویی
//   • لایه‌ی رویی ۲ سانتی که از هر سمتی که پایین‌تر است ۱٫۵ سانت جلو می‌آید (دماغه‌ی پله) — زیرش یک خطِ
//     سایه‌ی تیز می‌افتد که لبه‌ی پله را از دور مشخص می‌کند
//   • سایه‌ی تیز روی کفِ پایین‌تر، پای پیشانیِ پله، و سایه‌ی زیرِ دماغه روی خودِ پیشانی
// levels: { a, b } ارتفاعِ کفِ دو طرف؛ bottom: پایین‌ترین کفِ خانه (≤ ۰)؛ floorStyle: کفِ اتاقِ بالاتر
const THRESHOLD_TOP_CM = 2;
const NOSING_CM = 1.5;
const STEP_SHADOW_FLOOR_CM = 14;
const STEP_SHADOW_FLOOR_OPACITY = 0.45;
const STEP_SHADOW_RISER_OPACITY = 0.5;

export function ThresholdBlock({ length, thickness, centerAlong, bottom = 0, levels, floorStyle }) {
  const la = levels?.a || 0;
  const lb = levels?.b || 0;
  const upper = Math.max(la, lb);
  const top = upper + 0.5; // هم‌سطحِ رویه‌ی کفِ بالاتر (کف‌ها ۰٫۵ سانت بالای سطحشان رندر می‌شوند)
  const lowA = la < upper - 0.5;
  const lowB = lb < upper - 0.5;
  if (length <= 0 || (!lowA && !lowB && bottom >= 0)) return null;

  const faceAbs = thickness / 2 + FACE_OFFSET_CM;
  const depth = faceAbs * 2;
  const slabBottom = top - THRESHOLD_TOP_CM;
  const bodyH = slabBottom - bottom;
  // سنگِ رویی: از هر سمتِ پایین‌تر، دماغه جلو می‌آید
  const frontA = faceAbs + (lowA ? NOSING_CM : 0);
  const frontB = faceAbs + (lowB ? NOSING_CM : 0);
  const slabDepth = frontA + frontB;
  const slabZ = (frontA - frontB) / 2;

  const sides = [
    { key: 'a', low: lowA, level: la, sign: 1 },
    { key: 'b', low: lowB, level: lb, sign: -1 },
  ];

  return (
    <group>
      {bodyH > 0.01 && (
        <FloorMaterialBox
          position={[centerAlong, bottom + bodyH / 2, 0]}
          size={[length, bodyH, depth]}
          layoutX={length}
          layoutY={Math.max(bodyH, depth)}
          style={floorStyle}
        />
      )}
      <FloorMaterialBox
        position={[centerAlong, slabBottom + THRESHOLD_TOP_CM / 2, slabZ]}
        size={[length, THRESHOLD_TOP_CM, slabDepth]}
        layoutX={length}
        layoutY={slabDepth}
        style={floorStyle}
      />
      {sides.map((sd) => {
        if (!sd.low) return null;
        const riserH = top - (sd.level + 0.5);
        const rotY = sd.sign > 0 ? 0 : Math.PI;
        const underH = Math.min(6, riserH * 0.6);
        return (
          <group key={sd.key}>
            {/* سایه‌ی پای پله روی کفِ پایین‌تر — تیره کنارِ پیشانی، محو به‌سمتِ اتاق */}
            <AoStrip
              position={[centerAlong, sd.level + 0.66, sd.sign * (faceAbs + STEP_SHADOW_FLOOR_CM / 2)]}
              rotation={[-Math.PI / 2, 0, sd.sign > 0 ? Math.PI : 0]}
              size={[length, STEP_SHADOW_FLOOR_CM]}
              opacity={STEP_SHADOW_FLOOR_OPACITY}
            />
            {/* سایه‌ی زیرِ دماغه روی خودِ پیشانی — تیره بالا، محو به پایین */}
            {underH > 0.5 && (
              <AoStrip
                position={[centerAlong, slabBottom - underH / 2, sd.sign * (faceAbs + 0.05)]}
                rotation={[0, rotY, Math.PI]}
                size={[length, underH]}
                opacity={STEP_SHADOW_RISER_OPACITY}
              />
            )}
          </group>
        );
      })}
    </group>
  );
}

export default WallSegment;
// «چارچوبِ بازشو» — ورودیِ بینِ دو فضا که در ندارد (مثلاً پذیرایی ↔ آشپزخانه). مثلِ اجرای واقعی:
//   • نعل‌درگاه: دیوار بالای بازشو (در Create3D.jsx — OPENING_HEIGHT_CM)
//   • روکشِ داخلِ بازشو (دو کنار + سقفِ بازشو) که لبه‌ی خامِ دیوار را می‌پوشاند
//   • قاب (زهوار) روی هر دو رویِ دیوار، ۷ سانت عرض و ۱٫۵ سانت برجسته
// style: 'white' (پیش‌فرض، قابِ سفیدِ رنگ‌شده) | 'wood' (چوبی) | 'plaster' (فقط روکشِ گچی، بدونِ قاب)
export const OPENING_FRAME_COLORS = { white: '#f2efe8', wood: '#8a5a3b', plaster: '#ebe7df' };
const OF_LINING_CM = 1.5;
const OF_CASING_W = 7;
const OF_CASING_D = 1.5;

// onSelect: لمس (در نمای پیش‌فرض) → پنلِ تنظیمِ بازشو. isSelected: نوارِ آبیِ انتخاب کفِ دهانه
export function OpeningFrame({ width, height, thickness, centerAlong, base = 0, style = 'white', onSelect, isSelected }) {
  if (width <= 0 || height <= 0) return null;
  const color = OPENING_FRAME_COLORS[style] || OPENING_FRAME_COLORS.white;
  const rough = style === 'wood' ? 0.55 : 0.75;
  const depth = thickness + FACE_OFFSET_CM * 2;
  const faceAbs = thickness / 2 + FACE_OFFSET_CM;
  const mat = <meshStandardMaterial color={color} roughness={rough} metalness={0} envMapIntensity={0.3} />;
  const top = base + height;
  const handleClick = onSelect
    ? (e) => {
        e.stopPropagation();
        onSelect();
      }
    : undefined;
  return (
    <group onClick={handleClick}>
      {/* سطحِ نامرئی داخلِ دهانه فقط برای لمس (در خروجیِ رندر نیست — کاملاً شفاف) */}
      {onSelect && (
        <mesh position={[centerAlong, base + height / 2, 0]}>
          <boxGeometry args={[width, height, thickness]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>
      )}
      {isSelected && (
        <mesh position={[centerAlong, base + 1, 0]}>
          <boxGeometry args={[width, 2, thickness + 8]} />
          <meshBasicMaterial color="#0a66c2" />
        </mesh>
      )}
      {/* روکشِ دو کنار و سقفِ بازشو */}
      {[-1, 1].map((sx) => (
        <mesh key={`l${sx}`} position={[centerAlong + sx * (width / 2 - OF_LINING_CM / 2), base + height / 2, 0]}>
          <boxGeometry args={[OF_LINING_CM, height, depth]} />
          {mat}
        </mesh>
      ))}
      <mesh position={[centerAlong, top - OF_LINING_CM / 2, 0]}>
        <boxGeometry args={[width, OF_LINING_CM, depth]} />
        {mat}
      </mesh>
      {/* قاب روی دو رویِ دیوار */}
      {style !== 'plaster' &&
        [-1, 1].map((sz) => (
          <group key={`c${sz}`}>
            {[-1, 1].map((sx) => (
              <mesh key={sx} position={[centerAlong + sx * (width / 2 + OF_CASING_W / 2 - OF_LINING_CM), base + (height + OF_CASING_W - OF_LINING_CM) / 2, sz * (faceAbs + OF_CASING_D / 2)]}>
                <boxGeometry args={[OF_CASING_W, height + OF_CASING_W - OF_LINING_CM, OF_CASING_D]} />
                {mat}
              </mesh>
            ))}
            <mesh position={[centerAlong, top + OF_CASING_W / 2 - OF_LINING_CM, sz * (faceAbs + OF_CASING_D / 2)]}>
              <boxGeometry args={[width + (OF_CASING_W - OF_LINING_CM) * 2, OF_CASING_W, OF_CASING_D]} />
              {mat}
            </mesh>
          </group>
        ))}
    </group>
  );
}
